import { assertNotEmpty, typeUtils } from '@festina-life/core'
import { Button, ButtonContainer } from '@festina-life/ui/actions'
import {
  FormikForm,
  FormikSelect,
  FormikTextInput,
} from '@festina-life/ui/formikForms'
import { useTranslate } from '@festina-life/ui/hooks'
import { Modal } from '@festina-life/ui/structure'
import { useId, useMemo } from 'react'
import * as Yup from 'yup'

import { useTemplateContext } from '../../context/templateContext'
import { useLazyLayerMetaModel } from '../../context/useLazyLayerMetaModel'
import type {
  SubTemplateValueSetupField,
  SubTemplateValueSetupFieldValue,
} from '../../types/templateModel'
import {
  generateEmptyFieldValue,
  newEmptyVariantFromVariantType,
} from '../../uiModels/createEmptyFieldHelpers'

type Props = {
  forField: SubTemplateValueSetupField
  isIdentifiable: boolean
  onClose: () => void
}

type CreateSubtemplatedValueSetupInput = {
  typeName: string
  identifier?: string
}

const validateKey =
  typeUtils.createValidator<CreateSubtemplatedValueSetupInput>()

export const AddSubtemplatedVariantModal = ({
  forField,
  onClose,
  isIdentifiable,
}: Props) => {
  const translate = useTranslate()
  const formId = useId()
  const { templateVersion } = useTemplateContext()
  const templateModel = useLazyLayerMetaModel(templateVersion?.layer.layerId)
  assertNotEmpty(templateVersion)

  const fieldValue = useMemo(
    () =>
      forField.value ??
      (generateEmptyFieldValue({
        field: forField,
        meta: templateModel,
      }) as SubTemplateValueSetupFieldValue),
    [forField, templateModel],
  )
  const variantTypeNames = (forField.value?.variants ?? []).map(
    (variant) => variant.modelType?.typeName,
  )

  const options = useMemo(
    () =>
      fieldValue.availableVariantTypes
        .map((type) => type.typeName)
        .filter(
          (typeName) => isIdentifiable || !variantTypeNames.includes(typeName),
        )
        .sort(),
    [fieldValue.availableVariantTypes, isIdentifiable, variantTypeNames],
  )

  const initialValues = {
    typeName: options[0],
    identifier: '',
  } satisfies CreateSubtemplatedValueSetupInput

  const validationSchema: Yup.ObjectSchema<CreateSubtemplatedValueSetupInput> =
    Yup.object({
      typeName: Yup.string()
        .required()
        .test(
          'unique',
          translate('validation.unique'),
          (value) =>
            !fieldValue.variants.find((v) => v.subTemplateIdentifier === value),
        ),
      identifier: isIdentifiable
        ? Yup.string()
            .required()
            .min(2, translate('validation.length', { length: 2 }))
            .test(
              'unique',
              translate('validation.unique'),
              (value) =>
                !forField.value?.variants.find(
                  (v) => v.subTemplateIdentifier === value,
                ),
            )
        : Yup.string().optional(),
    })

  const handleSubmit = async (values: CreateSubtemplatedValueSetupInput) => {
    const selectedVariantType = fieldValue.availableVariantTypes.find(
      (v) => v.typeName === values.typeName,
    )
    assertNotEmpty(selectedVariantType)

    const typedUpdate = forField.update as (
      value: SubTemplateValueSetupFieldValue,
    ) => Promise<void>

    const nextVariant = newEmptyVariantFromVariantType({
      path: `${forField.path}{${values.typeName}}`,
      selectedType: selectedVariantType,
      meta: templateModel,
      isParentNotRelevant: false,
      updateField: (nextValue) => {
        const nextVariants = [...fieldValue.variants]
        const variantIndex = nextVariants.findIndex(
          (v) => v.subTemplateIdentifier === nextValue.subTemplateIdentifier,
        )
        nextVariants.splice(variantIndex, 1, nextValue)
        return typedUpdate({ ...fieldValue, variants: nextVariants })
      },
    })

    nextVariant.subTemplateIdentifier = isIdentifiable
      ? (values.identifier ?? '')
      : values.typeName

    await typedUpdate({
      ...fieldValue,
      variants: [...fieldValue.variants, nextVariant],
    })

    onClose()
  }

  return (
    <Modal isOpen onClose={onClose}>
      <Modal.Title
        title={
          isIdentifiable
            ? translate(
                'templateViewer.addIdentifiableSubtemplatedVariantModal.title',
              )
            : translate(
                'templateViewer.addSubclassedSubtemplatedVariantModal.title',
              )
        }
      />
      <Modal.Body outerClassName="max-w-2xl">
        <div className="mb-4 grid grid-cols-[auto_1fr] gap-y-4">
          <div>
            {`${translate('templateViewer.addSubtemplatedVariantModal.path')}:`}
          </div>
          <div className="text-right">{forField.path}</div>
          <div>
            {`${translate(
              'templateViewer.addSubtemplatedVariantModal.modelTypeName',
            )}:`}
          </div>
          <div className="text-right">
            {forField.originalPropertyType?.typeName}
          </div>
        </div>
        <FormikForm
          formId={formId}
          initialValues={initialValues}
          onSubmit={handleSubmit}
          validationSchema={validationSchema}
        >
          <FormikSelect
            options={options}
            name={validateKey('typeName')}
            label={translate(
              'templateViewer.addSubtemplatedVariantModal.templateType.label',
            )}
            disabled={options.length === 1}
          />
          {isIdentifiable && (
            <FormikTextInput
              name={validateKey('identifier')}
              label={translate(
                'templateViewer.addSubtemplatedVariantModal.identifier.label',
              )}
              isRequired
            />
          )}
        </FormikForm>
      </Modal.Body>
      <Modal.Footer>
        <ButtonContainer>
          <Button variant="secondary" onClick={onClose}>
            {translate('close')}
          </Button>
          <Button type="submit" variant="primary" form={formId}>
            {translate('create')}
          </Button>
        </ButtonContainer>
      </Modal.Footer>
    </Modal>
  )
}
